// Track-position strip: the replay lap's speed trace on the SAME time axis
// as the damper-pos charts, joined to their uPlot cursor-sync group — hover
// anywhere on a waveform and the strip's crosshair shows WHERE on the lap
// that sample is (lap distance + speed readout). Engineers read a circuit
// by its speed-vs-time/distance shape, so this is the lap map.
//
// Data comes from GET /api/tests/{id}/track_position (presentation sidecar
// next to the cached replay lap). Non-replay studies and laps without a
// sidecar degrade: nothing renders for the former, a one-line hint for the
// latter (the data is buildable — scripts.backfill_replay_position_sidecars).
import { useEffect, useRef, useState } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

import {
  fetchTrackPosition,
  type TrackPositionAvailable,
  type TrackPositionResponse,
} from "../api/track_position";
import {
  ZOOM_GROUP, broadcastReset, broadcastZoom, isBroadcasting,
  joinZoomGroup, runSilently, type XRange, type ZoomMember,
} from "./zoomSync";

const MPS_TO_KMH = 3.6;
const STRIP_HEIGHT = 110;
const STRIP_WIDTH = 800;
const MIN_STRIP_WIDTH = 320;
const SPEED_COLOUR = "rgba(155, 89, 182, 0.9)";

type StripState =
  | { kind: "loading" }
  | { kind: "hidden" }               // not a replay study — render nothing
  | { kind: "no-sidecar"; reason: string }
  | { kind: "ready"; pos: TrackPositionAvailable };

export type TrackPositionStripProps = {
  testId: number;
};

export function TrackPositionStrip({ testId }: TrackPositionStripProps) {
  const [state, setState] = useState<StripState>({ kind: "loading" });
  const hostRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<uPlot | null>(null);
  const memberRef = useRef<ZoomMember | null>(null);
  const [cursorIdx, setCursorIdx] = useState<number | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setState({ kind: "loading" });
    (async () => {
      try {
        const resp: TrackPositionResponse = await fetchTrackPosition(testId, {
          maxPoints: 1200, signal: ctrl.signal,
        });
        if (ctrl.signal.aborted) return;
        if (resp.available) {
          setState({ kind: "ready", pos: resp });
        } else if (resp.reason.includes("not a track-replay")) {
          setState({ kind: "hidden" });
        } else {
          setState({ kind: "no-sidecar", reason: resp.reason });
        }
      } catch {
        if (!ctrl.signal.aborted) setState({ kind: "hidden" });
      }
    })();
    return () => ctrl.abort();
  }, [testId]);

  useEffect(() => {
    if (state.kind !== "ready" || !hostRef.current) return;
    const host = hostRef.current;
    const { pos } = state;
    const speedKmh = pos.speed_mps.map((v) => v * MPS_TO_KMH);
    const fullRange = (): XRange => ({
      min: pos.time_s[0],
      max: pos.time_s[pos.time_s.length - 1],
    });
    const width = Math.max(
      MIN_STRIP_WIDTH,
      Math.floor(host.getBoundingClientRect().width || STRIP_WIDTH),
    );
    const opts: uPlot.Options = {
      width,
      height: STRIP_HEIGHT,
      series: [
        {},
        {
          label: "speed",
          stroke: SPEED_COLOUR,
          width: 1.5,
          fill: "rgba(155, 89, 182, 0.12)",
        },
      ],
      // Join the damper charts' sync group: hovering any waveform lane
      // drives this crosshair (and vice versa) because both share the
      // replay-window time axis. cursor.sync carries the crosshair; the zoom
      // module carries the drag-zoom scale.
      cursor: {
        sync: { key: ZOOM_GROUP },
        y: false,
        drag: { setScale: true, x: true, y: false },
        bind: {
          dblclick: (_self: uPlot) => (_e: MouseEvent) => {
            broadcastReset(ZOOM_GROUP);
            return null;
          },
        },
      },
      scales: { x: { time: false } },
      axes: [
        { stroke: "#9a9a9a", grid: { stroke: "#2a2a2a", dash: [2, 2] } },
        {
          stroke: "#9a9a9a",
          grid: { stroke: "#2a2a2a", dash: [2, 2] },
          label: "speed (km/h)",
        },
      ],
      legend: { show: false },
      hooks: {
        setCursor: [
          (self: uPlot) => {
            const idx = self.cursor.idx;
            setCursorIdx(typeof idx === "number" ? idx : null);
          },
        ],
        setScale: [
          (self: uPlot, key: string) => {
            if (key !== "x" || isBroadcasting()) return;
            const member = memberRef.current;
            if (!member) return;
            const s = self.scales.x;
            if (s?.min == null || s?.max == null) return;
            broadcastZoom(ZOOM_GROUP, member, { min: s.min, max: s.max });
          },
        ],
      },
    };
    // Construct silently so the initial setScale does not broadcast a zoom.
    let plot!: uPlot;
    runSilently(() => {
      plot = new uPlot(
        opts,
        [pos.time_s, speedKmh] as unknown as uPlot.AlignedData,
        host,
      );
    });
    plotRef.current = plot;

    const member: ZoomMember = {
      applyX: (range: XRange | null) => {
        const p = plotRef.current;
        if (!p) return;
        runSilently(() => p.setScale("x", range ?? fullRange()));
      },
    };
    memberRef.current = member;
    const leaveGroup = joinZoomGroup(ZOOM_GROUP, member);

    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        plotRef.current?.setSize({
          width: Math.max(MIN_STRIP_WIDTH, Math.floor(e.contentRect.width)),
          height: STRIP_HEIGHT,
        });
      }
    });
    ro.observe(host);

    return () => {
      ro.disconnect();
      leaveGroup();
      memberRef.current = null;
      plot.destroy();
      plotRef.current = null;
    };
  }, [state]);

  if (state.kind === "hidden" || state.kind === "loading") return null;

  if (state.kind === "no-sidecar") {
    return (
      <p
        className="muted"
        style={{ fontSize: "11px", marginTop: "4px" }}
        data-testid="track-position-missing"
      >
        Track-position strip unavailable for this lap ({state.reason}).
      </p>
    );
  }

  const { pos } = state;
  const idx = cursorIdx;
  const atCursor =
    idx !== null && idx >= 0 && idx < pos.time_s.length
      ? {
          dist: pos.lap_dist_m[idx],
          speed: pos.speed_mps[idx] * MPS_TO_KMH,
          t: pos.time_s[idx],
        }
      : null;

  return (
    <div className="track-position-strip" data-testid="track-position-strip">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          fontSize: "12px",
        }}
      >
        <span className="muted">
          Track position · {pos.track_code} · lap “{pos.lap.replace(/^replay_/, "")}”
          {pos.lap_length_m != null && ` · ${Math.round(pos.lap_length_m)} m`}
        </span>
        <span
          data-testid="track-position-readout"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {atCursor
            ? `D = ${Math.round(atCursor.dist)} m · ${atCursor.speed.toFixed(0)} km/h · t = ${atCursor.t.toFixed(2)} s`
            : "hover a chart to locate on lap"}
        </span>
      </div>
      <div ref={hostRef} />
    </div>
  );
}
